import AddIcon from '@mui/icons-material/Add';
import DialpadIcon from '@mui/icons-material/Dialpad';
import { Grid } from '@mui/material';
import Button from '@mui/material/Button';
import { useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useSelector } from 'react-redux';
import { SiteWithAcCredentialsSettingsResponse } from '../../apiRtk/be4feApi';
import { useGetLoadedParams } from '../../hooks/paramsHooks';
import { useModal } from '../../hooks/useModal';
import { getApartmentDevices } from '../../modules/apartments/selectors';
import { ApplicationContext } from '../../modules/commonTypes';
import { IUser } from '../../modules/user/store';
import { isUserGuest } from '../../modules/user/utils';
import RevokeUserPinDialog from '../RevokeUserPinModal';
import UserPinModalNewComponent from '../UserPinModalNew';
import { VisitorModalContext, VisitorModalOpeningContext } from '../VisitorModal/types';
import VisitorModal from '../VisitorModal/VisitorModal';
import CredentialAlert from './CredentialAlert';
import DormakabaAlert from './DormakabaAlert';
import { useGetGlobalHooks } from './hooks/getGlobalHooks';
import useDormakabaAccess from './hooks/useDormakabaAccess';
import messages from './messages';
import { PanelPIN } from './PanelPIN';
import SectionTitle from './SectionTitle';

interface ISectionPin {
  user: IUser;
  siteData: SiteWithAcCredentialsSettingsResponse;
  userPinData?: {
    active: boolean;
    id: string;
    pin: string;
  };
}

export interface EnrichedIUser extends IUser {
  validFrom?: string;
  validTo?: string;
}

function SectionPin({ siteData, user, userPinData }: ISectionPin) {
  const { formatMessage, isCompanyOrSiteAdmin } = useGetGlobalHooks();
  const { companyId, siteId, userId } = useGetLoadedParams(ApplicationContext.UserDetail);
  const isEmpty = !userPinData?.pin;
  const apartmentDevices = useSelector(getApartmentDevices).data || [];
  const isSiteWithDormakabaIntegrationEnabled = siteData?.integrations?.DORMAKABA?.enabled;
  const isUserPinLength6 = siteData?.services?.ACCESS_CONTROL?.userPinLength === 6;
  const isGuestPinLength6 = siteData?.services?.ACCESS_CONTROL?.guestPinLength === 6;
  const isVisitor = isUserGuest(user.role);
  const { Modal: RevokeUserPinModal, onOpen: onOpenRevokeUserPinModal } = useModal({
    Modal: RevokeUserPinDialog,
  });
  const { Modal: UserPinModalNew, onOpen: onOpenUserPinModal } = useModal({
    Modal: UserPinModalNewComponent,
  });
  const { Modal: VisitorUModal, onOpen: onOpenVisitorUModal } = useModal({
    Modal: VisitorModal,
  });

  const [displayInfoBox, setDisplayInfoBox] = useState(isEmpty);

  const toggleDisplayInfoBox = () => {
    setDisplayInfoBox((prevState) => !prevState);
  };

  const { enrichedUser, hasDormakabaAccessForUser } = useDormakabaAccess({
    companyId,
    siteId,
    user,
    userId,
  });

  return (
    <>
      <SectionTitle
        isEmpty={isEmpty}
        onInfoClick={toggleDisplayInfoBox}
        startIcon={<DialpadIcon color={isEmpty ? 'secondary' : 'primary'} />}
        title={formatMessage(messages.userAccessFormPinTitle)}
      />
      <DormakabaAlert
        companyId={String(companyId)}
        hasDormakabaAccessForUser={hasDormakabaAccessForUser}
        isGuestPinLength6={isGuestPinLength6}
        isUserPinLength6={isUserPinLength6}
        isVisitor={isVisitor}
        isSiteWithDormakabaIntegrationEnabled={isSiteWithDormakabaIntegrationEnabled}
        pinLength={userPinData?.pin ? Number(userPinData.pin.length) : undefined}
        siteId={String(siteId)}
      />
      {isEmpty ? (
        <Button
          color="primary"
          data-testid="user-access-generate-pin"
          onClick={isUserGuest(user.role) ? onOpenVisitorUModal : onOpenUserPinModal}
          startIcon={<AddIcon />}
          sx={{ mb: 2 }}
          variant="outlined"
        >
          <FormattedMessage {...messages.userAccessFormPinGenerateNewPin} />
        </Button>
      ) : (
        <PanelPIN concealable pin={userPinData?.pin} />
      )}
      {displayInfoBox && (
        <CredentialAlert
          infoText={formatMessage(messages.userAccessFormPinInfoDescription, {
            br: <br />,
          })}
        />
      )}
      {!isEmpty && (
        <Grid sx={{ mt: 4 }}>
          <Button
            color="error"
            data-testid="user-access-delete-pin"
            disabled={!isCompanyOrSiteAdmin}
            onClick={onOpenRevokeUserPinModal}
          >
            <FormattedMessage {...messages.revokeUserPinButtonDeletePin} />
          </Button>
        </Grid>
      )}
      <UserPinModalNew context="SITE_USER_DETAIL" user={user} />
      <RevokeUserPinModal user={user} />
      <VisitorUModal
        msgContext={VisitorModalContext.AA_MODAL_NEW_GUEST_PIN}
        apartmentInfo={{ companyId, siteId }}
        apartmentDevices={user?.apartments.length ? apartmentDevices : []}
        siteName={siteData?.name}
        user={enrichedUser}
        isRegenerate
        openingContext={VisitorModalOpeningContext.SECTION_PIN}
      />
    </>
  );
}

export default SectionPin;
